// ============================================================
//  src/pages/Receiver/HelpRequestForm.jsx
//  Smart Donation Management System
//  Sara code ek hi function ke andar hai
// ============================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, AlertCircle } from "lucide-react";
import "../../assets/css/receiverdashboard/CreateRequest.css";

 function CreateRequest() {

  // ══════════════════════════════════════
  //  CONSTANTS  (function ke andar)
  // ══════════════════════════════════════

  const CATEGORIES = [
    { value: "",         label: "— Select Category —", icon: ""   },
    { value: "food",     label: "Food",                icon: "🍲" },
    { value: "medicine", label: "Medicine",            icon: "💊" },
    { value: "blood",    label: "Blood",               icon: "🩸" },
  ];

  const URGENCY_LEVELS = [
    { key: "low",       label: "Low",       icon: "🟢", hint: "Can wait a few days"  },
    { key: "medium",    label: "Medium",    icon: "🔵", hint: "Needed within 24 hrs" },
    { key: "emergency", label: "Emergency", icon: "🚨", hint: "Needed immediately"   },
  ];

  const STEPS = [
    { step: "1", label: "Submit Request",    desc: "Fill the form and submit your need"       },
    { step: "2", label: "Verification",      desc: "Our team reviews within 24 hours"         },
    { step: "3", label: "Matching",          desc: "We match you with available donors"       },
    { step: "4", label: "Delivery / Pickup", desc: "Item delivered to your drop-off location" },
  ];

  const DESC_MIN = 10;
  const DESC_MAX = 300;

  // ══════════════════════════════════════
  //  STATE
  // ══════════════════════════════════════

  const navigate = useNavigate();

  const [form, setForm] = useState({
    category:    "",
    urgency:     "",
    quantity:    "",
    description: "",
    location:    "",
  });

  const [errors,    setErrors]    = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [requestId] = useState(
    () => "REQ-" + Date.now().toString(36).toUpperCase().slice(-6)
  );

  // ══════════════════════════════════════
  //  HANDLERS
  // ══════════════════════════════════════

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = () => {
    const errs = {};
    if (!form.category)    errs.category    = "Please select a donation category.";
    if (!form.urgency)     errs.urgency     = "Please select an urgency level.";
    const qty = Number(form.quantity);
    if (!form.quantity || isNaN(qty) || qty < 1)
                           errs.quantity    = "Please enter a valid quantity (minimum 1).";
    if (!form.description || form.description.trim().length < DESC_MIN)
                           errs.description = `Description must be at least ${DESC_MIN} characters.`;
    if (!form.location.trim())
                           errs.location    = "Please enter a drop-off location.";

    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  };

  const handleBack = () => navigate("/receiver/dashboard");

  // ══════════════════════════════════════
  //  RENDER HELPERS  (JSX blocks as variables)
  // ══════════════════════════════════════

  // ── Page Header ──
  const renderPageHeader = (
    <div className="hrf-page-header">
      <div className="hrf-header-left">
        <button className="hrf-back-link" onClick={handleBack}>
          <ArrowLeft size={18} />
          <span>Back to Dashboard</span>
        </button>
        <div>
          <h1 className="hrf-page-title">Create New Request</h1>
          <p className="hrf-page-subtitle">
            Fill in the details below — our team will verify your request shortly.
          </p>
        </div>
      </div>
      <div className="hrf-header-badge">
        <span>📋</span>
        <span>Receiver Portal</span>
      </div>
    </div>
  );

  // ── Category Dropdown ──
  const renderCategory = (
    <div className="hrf-field-group">
      <label className="hrf-label">
        <span className="hrf-label-dot" />
        Donation Category
      </label>
      <div className="hrf-select-wrapper">
        <select
          className={`hrf-input hrf-select ${errors.category ? "hrf-input-error" : ""}`}
          value={form.category}
          onChange={(e) => handleChange("category", e.target.value)}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.icon ? `${cat.icon}  ${cat.label}` : cat.label}
            </option>
          ))}
        </select>
        <span className="hrf-select-arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
      {errors.category && (
        <p className="hrf-error-msg"><AlertCircle size={12} /> {errors.category}</p>
      )}
    </div>
  );

  // ── Urgency Buttons ──
  const renderUrgency = (
    <div className="hrf-field-group">
      <label className="hrf-label">
        <span className="hrf-label-dot" />
        Urgency Level
      </label>
      <div className="hrf-urgency-group">
        {URGENCY_LEVELS.map((level) => (
          <button
            key={level.key}
            type="button"
            title={level.hint}
            className={[
              "hrf-urgency-btn",
              `hrf-urgency-${level.key}`,
              form.urgency === level.key ? "hrf-urgency-active" : "",
            ].join(" ")}
            onClick={() => handleChange("urgency", level.key)}
          >
            <span className="hrf-urgency-icon">{level.icon}</span>
            <span className="hrf-urgency-label">{level.label}</span>
            <span className="hrf-urgency-hint">{level.hint}</span>
          </button>
        ))}
      </div>
      {errors.urgency && (
        <p className="hrf-error-msg" style={{ marginTop: 8 }}>
          <AlertCircle size={12} /> {errors.urgency}
        </p>
      )}
    </div>
  );

  // ── Quantity Field ──
  const renderQuantity = (
    <div className="hrf-field-group">
      <label className="hrf-label">
        <span className="hrf-label-dot" />
        Quantity Required
      </label>
      <input
        type="number"
        min={1}
        placeholder="e.g. 5  (units / kg / bags)"
        className={`hrf-input ${errors.quantity ? "hrf-input-error" : ""}`}
        value={form.quantity}
        onChange={(e) => handleChange("quantity", e.target.value)}
      />
      {errors.quantity && (
        <p className="hrf-error-msg"><AlertCircle size={12} /> {errors.quantity}</p>
      )}
    </div>
  );

  // ── Description Field ──
  const renderDescription = (
    <div className="hrf-field-group">
      <label className="hrf-label">
        <span className="hrf-label-dot" />
        Description
      </label>
      <textarea
        rows={4}
        maxLength={DESC_MAX}
        placeholder="Describe your need — type, condition, timing preferences, etc."
        className={`hrf-input hrf-textarea ${errors.description ? "hrf-input-error" : ""}`}
        value={form.description}
        onChange={(e) => handleChange("description", e.target.value)}
      />
      <div className="hrf-char-row">
        {errors.description
          ? <p className="hrf-error-msg"><AlertCircle size={12} /> {errors.description}</p>
          : <span />
        }
        <span className="hrf-char-count">{form.description.length}/{DESC_MAX}</span>
      </div>
    </div>
  );

  // ── Location Field ──
  const renderLocation = (
    <div className="hrf-field-group">
      <label className="hrf-label">
        <span className="hrf-label-dot" />
        Drop-off Location
      </label>
      <input
        type="text"
        placeholder="e.g. Street 5, Gulshan-e-Ravi, Lahore"
        className={`hrf-input ${errors.location ? "hrf-input-error" : ""}`}
        value={form.location}
        onChange={(e) => handleChange("location", e.target.value)}
      />
      {errors.location && (
        <p className="hrf-error-msg"><AlertCircle size={12} /> {errors.location}</p>
      )}
    </div>
  );

  // ── Right Info Panel ──
  const renderInfoPanel = (
    <div className="hrf-info-panel">

      {/* How it works */}
      <div className="hrf-info-card">
        <h3 className="hrf-info-card-title">📌 How It Works</h3>
        <div className="hrf-steps">
          {STEPS.map((s) => (
            <div key={s.step} className="hrf-step-item">
              <div className="hrf-step-num">{s.step}</div>
              <div>
                <p className="hrf-step-label">{s.label}</p>
                <p className="hrf-step-desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Urgency Guide */}
      <div className="hrf-info-card">
        <h3 className="hrf-info-card-title">⚡ Urgency Guide</h3>
        <div className="hrf-urgency-guide">
          <div className="hrf-guide-item hrf-guide-low">
            <span>🟢 Low</span>
            <p>Can wait 3–5 days. Regular need, no immediate risk.</p>
          </div>
          <div className="hrf-guide-item hrf-guide-medium">
            <span>🔵 Medium</span>
            <p>Needed within 24–48 hrs. Important but not critical.</p>
          </div>
          <div className="hrf-guide-item hrf-guide-emergency">
            <span>🚨 Emergency</span>
            <p>Immediate need. Life or health at risk. Top priority.</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="hrf-stats-card">
        <div className="hrf-stat-chip">
          <span className="hrf-stat-num">2.4K+</span>
          <span className="hrf-stat-lbl">Requests Fulfilled</span>
        </div>
        <div className="hrf-stat-chip">
          <span className="hrf-stat-num">98%</span>
          <span className="hrf-stat-lbl">Satisfaction Rate</span>
        </div>
        <div className="hrf-stat-chip">
          <span className="hrf-stat-num">&lt;24h</span>
          <span className="hrf-stat-lbl">Avg. Response Time</span>
        </div>
      </div>

    </div>
  );

  // ── Success Modal ──
  const renderSuccessModal = submitted && (
    <div
      className="hrf-modal-overlay"
      onClick={handleBack}
      role="dialog"
      aria-modal="true"
      aria-labelledby="hrf-success-title"
    >
      <div className="hrf-success-card" onClick={(e) => e.stopPropagation()}>

        {/* Green Top */}
        <div className="hrf-success-top">
          <div className="hrf-check-ring">
            <span className="hrf-check-icon">✅</span>
          </div>
          <h3 className="hrf-success-heading" id="hrf-success-title">
            Request Submitted!
          </h3>
          <span className="hrf-success-tag">Under Review</span>
        </div>

        {/* Body */}
        <div className="hrf-success-body">
          <p className="hrf-success-msg">
            Aapki request verification ke liye bhej di gayi hai.
            Hum jald aap se raabta karenge.
          </p>

          <div className="hrf-request-id-chip">
            <span>🔖</span>
            Request ID: <strong>{requestId}</strong>
          </div>

          <div className="hrf-success-info-row">
            <div className="hrf-info-item">
              <span className="hrf-info-icon">⏱</span>
              <div>
                <p className="hrf-info-label">Response Time</p>
                <p className="hrf-info-value">Within 24 hours</p>
              </div>
            </div>
            <div className="hrf-info-item">
              <span className="hrf-info-icon">📬</span>
              <div>
                <p className="hrf-info-label">Notification</p>
                <p className="hrf-info-value">Via App & Email</p>
              </div>
            </div>
          </div>

          <button className="hrf-back-to-dash-btn" onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>

      </div>
    </div>
  );

  // ══════════════════════════════════════
  //  MAIN RETURN
  // ══════════════════════════════════════

  return (
    <div className="hrf-page-container">
      

        {renderPageHeader}

        <div className="hrf-content-grid">

          {/* ── LEFT: Form Card ── */}
          <div className="hrf-form-card">
            <div className="hrf-form-card-header">
              <h2 className="hrf-form-card-title">Request Details</h2>
              <p className="hrf-form-card-sub">All fields marked with • are required</p>
            </div>

            <div className="hrf-form-body">
              {renderCategory}
              {renderUrgency}
              <div className="hrf-divider" />
              {renderQuantity}
              {renderDescription}
              {renderLocation}

              <button className="hrf-submit-btn" onClick={handleSubmit}>
                <Send size={17} />
                Submit Request
                <span className="hrf-submit-arrow">→</span>
              </button>
            </div>
          </div>

          {/* ── RIGHT: Info Panel ── */}
          {renderInfoPanel}

        </div>

        {renderSuccessModal}

      
    </div>
  );

}
export default CreateRequest
